"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { VoiceGuideButton } from "@/components/voice/VoiceGuideButton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { usePageTitle } from "@/lib/usePageTitle";
import { useCheckout } from "@/hooks/useCheckout";
import {
  PRICING_PLANS,
  TRIAL_DAYS,
  type PricingPlanId,
  getServerLimitForSubscription,
  type SubscriptionTier,
  type SubscriptionStatus,
} from "@/lib/billing/plans";

function tierLabel(tier: string) {
  switch (tier) {
    case "starter": return "Founder Starter";
    case "plus": return "Founder Plus";
    case "pro": return "Founder Pro";
    case "enterprise": return "Enterprise";
    default: return "Free";
  }
}

const PLAN_ORDER = ["starter", "plus", "pro"];
function planIndex(tier: string) {
  return PLAN_ORDER.indexOf(tier);
}

function StatusBadge({ status, cancelAtPeriodEnd }: { status: SubscriptionStatus; cancelAtPeriodEnd?: boolean | null }) {
  if (cancelAtPeriodEnd) {
    return (
      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        Cancels at period end
      </span>
    );
  }
  if (status === "active") {
    return (
      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
        Active
      </span>
    );
  }
  if (status === "trialing") {
    return (
      <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        Trial
      </span>
    );
  }
  if (status === "past_due") {
    return (
      <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
        Past Due
      </span>
    );
  }
  return null;
}

function CheckIcon() {
  return (
    <svg className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
    </svg>
  );
}

export default function BillingSettingsPage() {
  usePageTitle("Billing");

  const summary = useQuery(api.billing.getMySummary);
  const entitlement = useQuery(api.billing.getMyEntitlement);
  const openPortal = useAction(api.actions.openCustomerPortal);
  const cancelSub = useAction(api.actions.cancelSubscription);
  const resumeSub = useAction(api.actions.resumeSubscription);
  const { startCheckout, loadingPlan, error: checkoutError } = useCheckout();

  const [billingAction, setBillingAction] = useState<"cancel" | "resume" | "portal" | null>(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Plan change confirmation state
  const [pendingPlanChange, setPendingPlanChange] = useState<{
    planId: PricingPlanId;
    isUpgrade: boolean;
    isDowngrade: boolean;
    isTrialEligible: boolean;
  } | null>(null);

  if (!summary) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-pulse text-muted">Loading billing...</div>
      </div>
    );
  }

  const tier = summary.tier as SubscriptionTier;
  const status = summary.status as SubscriptionStatus;
  const serverLimit = getServerLimitForSubscription(tier, status);
  const hasSubscription = !!summary.subscriptionId;
  const isActiveSubscriber = hasSubscription && (status === "active" || status === "trialing");
  const isTrialing = summary.isTrialing;

  // Grace period data from entitlement query
  const isInGracePeriod = entitlement?.isInGracePeriod ?? false;
  const graceEndsAt = (entitlement as { graceEndsAt?: number | null } | null | undefined)?.graceEndsAt ?? null;
  const gracePeriodDays = (entitlement as { gracePeriodDays?: number } | null | undefined)?.gracePeriodDays ?? 3;

  const periodEnd = summary.currentPeriodEnd
    ? new Date(summary.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;

  const graceEndDate = graceEndsAt
    ? new Date(graceEndsAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;

  // Request plan change — show confirmation for existing subscribers; go direct for free tier
  const handleSelectPlan = (planId: PricingPlanId) => {
    setError("");
    setSuccessMessage("");
    const isTrialEligible = planId === "starter" && !summary.hasUsedTrial && tier === "free";

    if (isActiveSubscriber) {
      const currentIdx = planIndex(tier);
      const targetIdx = planIndex(planId);
      setPendingPlanChange({
        planId,
        isUpgrade: targetIdx > currentIdx,
        isDowngrade: targetIdx < currentIdx,
        isTrialEligible: false,
      });
    } else {
      // Non-subscriber: go directly to checkout
      startCheckout(planId, { withTrial: isTrialEligible });
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!pendingPlanChange) return;
    const { planId, isTrialEligible } = pendingPlanChange;
    setPendingPlanChange(null);

    await startCheckout(planId, {
      withTrial: isTrialEligible,
      onPlanUpdated: () => {
        const planDef = PRICING_PLANS.find((p) => p.id === planId);
        setSuccessMessage(`Your plan has been updated to ${planDef?.name ?? planId}. Changes are live immediately.`);
      },
    });
  };

  const handleCancel = async () => {
    setError("");
    setSuccessMessage("");
    setBillingAction("cancel");
    try {
      const result = await cancelSub();
      if (!result.ok) throw new Error(result.error?.message || "Cancel failed");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel.");
    } finally {
      setBillingAction(null);
    }
  };

  const handleResume = async () => {
    setError("");
    setSuccessMessage("");
    setBillingAction("resume");
    try {
      const result = await resumeSub();
      if (!result.ok) throw new Error(result.error?.message || "Resume failed");
      setSuccessMessage("Auto-renew has been re-enabled. Your subscription will continue past the current period.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resume.");
    } finally {
      setBillingAction(null);
    }
  };

  const handlePortal = async () => {
    setError("");
    setSuccessMessage("");
    setBillingAction("portal");
    try {
      const result = await openPortal({});
      if (!result.ok) throw new Error(result.error?.message || "Portal failed");
      if (result.data?.url) window.location.href = result.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to open portal.");
    } finally {
      setBillingAction(null);
    }
  };

  // Build confirmation dialog text
  const buildConfirmText = () => {
    if (!pendingPlanChange) return { title: "", description: "", confirmText: "" };
    const planDef = PRICING_PLANS.find((p) => p.id === pendingPlanChange.planId);
    const planName = planDef?.name ?? pendingPlanChange.planId;

    if (pendingPlanChange.isUpgrade) {
      return {
        title: `Upgrade to ${planName}?`,
        description: `Your plan will be switched to ${planName} immediately at $${planDef?.priceMonthly.toFixed(2) ?? "?"}/mo. The change takes effect right away and your billing cycle resets.`,
        confirmText: `Upgrade to ${planName}`,
      };
    }
    if (pendingPlanChange.isDowngrade) {
      return {
        title: `Downgrade to ${planName}?`,
        description: `Your plan will switch to ${planName} at $${planDef?.priceMonthly.toFixed(2) ?? "?"}/mo. The change takes effect immediately with prorated credit applied. Some features may become unavailable.`,
        confirmText: `Downgrade to ${planName}`,
      };
    }
    return {
      title: `Switch to ${planName}?`,
      description: `Your plan will be switched to ${planName} immediately.`,
      confirmText: `Switch to ${planName}`,
    };
  };

  const confirmDialogText = buildConfirmText();

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-sm text-muted">Manage your subscription and payment method</p>
        </div>
        <Link to="/dashboard/settings" className="text-sm text-primary hover:underline">
          Back to Settings
        </Link>
      </div>

      {/* Contextual voice guide */}
      <VoiceGuideButton
        track="payments"
        label="Listen: how billing works"
        aria-label="Play voice guide about subscription and billing"
      />

      {/* Grace period warning */}
      {isInGracePeriod && graceEndDate && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
          <div className="flex items-start gap-3">
            <svg className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-orange-800 dark:text-orange-200">
                Grace period active — re-subscribe to keep your access
              </p>
              <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
                Your subscription has ended but you have a <span className="font-medium">{gracePeriodDays}-day grace period</span> until{" "}
                <span className="font-medium">{graceEndDate}</span>. Re-subscribe below to prevent your organizations from being archived.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success banner */}
      {successMessage && (
        <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm font-medium text-green-800 dark:text-green-200">{successMessage}</p>
        </div>
      )}

      {/* Error banner */}
      {(error || checkoutError) && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error || checkoutError}
        </div>
      )}

      {/* Trial banner */}
      {isTrialing && summary.trialEndsAt && (() => {
        const daysLeft = Math.max(0, Math.ceil((summary.trialEndsAt - Date.now()) / (1000 * 60 * 60 * 24)));
        const trialEndDate = new Date(summary.trialEndsAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
        return (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                Free Trial
              </span>
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining
              </span>
            </div>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              Your {TRIAL_DAYS}-day free trial is active. Your card on file will be charged automatically on{" "}
              <span className="font-medium">{trialEndDate}</span>. Cancel before then to avoid any charge.
            </p>
          </div>
        );
      })()}

      {/* Past due warning */}
      {status === "past_due" && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">Payment failed</p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
            Your last payment did not go through. Update your payment method via the customer portal below to restore uninterrupted access.
          </p>
        </div>
      )}

      {/* Archived orgs warning */}
      {summary.usage.archivedOrgs > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            You have {summary.usage.archivedOrgs} archived organization{summary.usage.archivedOrgs > 1 ? "s" : ""}.
          </p>
          <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
            Re-subscribe to restore access. Archived organizations are permanently deleted after 30 days.
          </p>
        </div>
      )}

      {/* Current subscription card */}
      <div className="rounded-lg border border-border bg-background p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">Current Subscription</h2>
          {hasSubscription
            ? <StatusBadge status={status} cancelAtPeriodEnd={summary.cancelAtPeriodEnd} />
            : (
              <span className="rounded-full bg-foreground/10 px-2.5 py-0.5 text-xs font-semibold text-muted">
                Free plan
              </span>
            )}
        </div>

        {hasSubscription ? (
          <>
            <p className="mb-4 text-sm text-muted">
              Plan: <span className="font-medium text-foreground">{tierLabel(tier)}</span>
              {isTrialing && (
                <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  Trial
                </span>
              )}
            </p>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-md border border-border bg-foreground/5 p-4">
                <div className="text-xs text-muted">Servers / Orgs used</div>
                <div className="mt-1 text-lg font-semibold">
                  {serverLimit > 0 ? `${summary.usage.activeOrgs} / ${serverLimit}` : "—"}
                </div>
              </div>
              <div className="rounded-md border border-border bg-foreground/5 p-4">
                <div className="text-xs text-muted">
                  {summary.cancelAtPeriodEnd ? "Access ends" : "Next renewal"}
                </div>
                <div className="mt-1 text-sm font-semibold">
                  {periodEnd ?? "—"}
                </div>
                {summary.cancelAtPeriodEnd && (
                  <div className="mt-0.5 text-xs text-muted">Then moved to free plan</div>
                )}
              </div>
              <div className="rounded-md border border-border bg-foreground/5 p-4">
                <div className="text-xs text-muted">Status</div>
                <div className="mt-1 text-sm font-semibold capitalize">{status}</div>
              </div>
            </div>

            {/* Cancel / Resume */}
            <div className="mt-5 border-t border-border pt-4">
              {summary.cancelAtPeriodEnd ? (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-medium">Auto-renew is off</p>
                    <p className="mt-0.5 text-xs text-muted">
                      Your access continues until <span className="font-medium">{periodEnd ?? "end of period"}</span>.
                      After that you&apos;ll be moved to the free plan.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="shrink-0"
                    onClick={handleResume}
                    disabled={billingAction !== null}
                  >
                    {billingAction === "resume" ? "Turning on..." : "Turn On Auto-Renew"}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-medium">Auto-renew is on</p>
                    <p className="mt-0.5 text-xs text-muted">
                      Your subscription renews automatically on{" "}
                      <span className="font-medium">{periodEnd ?? "your renewal date"}</span>.
                      Turn it off to stop future charges.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="shrink-0 border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:border-red-600 dark:hover:bg-red-900/20"
                    onClick={handleCancel}
                    disabled={billingAction !== null}
                  >
                    {billingAction === "cancel" ? "Turning off..." : "Turn Off Auto-Renew"}
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted">
            You&apos;re on the free plan. Subscribe below to unlock server management and listing features.
          </p>
        )}
      </div>

      {/* Payment Method card */}
      <div className="rounded-lg border border-border bg-background p-6">
        <h2 className="mb-3 text-lg font-semibold">Payment Information</h2>
        <p className="text-sm text-muted">
          Update your saved payment information, view invoices, and download receipts via the Polar customer portal.
        </p>
        <div className="mt-4">
          <Button
            onClick={handlePortal}
            disabled={billingAction !== null || !hasSubscription}
          >
            {billingAction === "portal" ? "Opening..." : "Open Customer Portal →"}
          </Button>
          {!hasSubscription && (
            <p className="mt-2 text-xs text-muted">Available once you have an active subscription.</p>
          )}
        </div>
      </div>

      {/* Plans card */}
      <div className="rounded-lg border border-border bg-background p-6">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Plans</h2>
          {isActiveSubscriber && (
            <p className="text-xs text-muted">Upgrades and downgrades take effect immediately.</p>
          )}
        </div>

        {!isActiveSubscriber && (
          <p className="mb-5 text-sm text-muted">
            Choose a plan to get started with a monthly billing cycle.
            {!summary.hasUsedTrial && (
              <> The Founder Starter plan includes a{" "}
                <span className="font-medium text-foreground">{TRIAL_DAYS}-day free trial</span> for first-time subscribers.
              </>
            )}
          </p>
        )}

        <div className="grid gap-4 lg:grid-cols-3">
          {PRICING_PLANS.map((plan) => {
            const isCurrent = (status === "active" || status === "trialing") && tier === (plan.id as SubscriptionTier);
            const isTrialEligible = plan.id === "starter" && !summary.hasUsedTrial && tier === "free";
            const hasUsedTrial = summary.hasUsedTrial && plan.id === "starter";

            const currentIdx = planIndex(tier);
            const targetIdx = planIndex(plan.id);
            const isUpgrade = isActiveSubscriber && targetIdx > currentIdx;
            const isDowngrade = isActiveSubscriber && targetIdx < currentIdx;

            let buttonLabel = "Subscribe";
            if (isTrialEligible) buttonLabel = `Start ${TRIAL_DAYS}-Day Free Trial`;
            else if (isUpgrade) buttonLabel = `Upgrade to ${plan.name}`;
            else if (isDowngrade) buttonLabel = `Downgrade to ${plan.name}`;
            else if (isActiveSubscriber) buttonLabel = "Switch Plan";

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-lg border p-5 transition-colors ${
                  isCurrent ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                }`}
              >
                {plan.badge && !isCurrent && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                    <span className="rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{plan.name}</span>
                  {isCurrent && (
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
                      Current
                    </span>
                  )}
                  {isTrialEligible && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      Trial eligible
                    </span>
                  )}
                  {hasUsedTrial && (
                    <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[11px] font-medium text-muted">
                      Trial used
                    </span>
                  )}
                </div>

                <div className="mb-1 text-2xl font-bold">${plan.priceMonthly.toFixed(2)}<span className="text-sm font-normal text-muted">/mo</span></div>
                <p className="mb-4 text-xs text-muted">{plan.description}</p>

                <ul className="mb-5 flex-1 space-y-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-xs text-muted">
                      <CheckIcon />
                      {feature}
                    </li>
                  ))}
                  {plan.id === "starter" && !summary.hasUsedTrial ? (
                    <li className="flex items-start gap-2 text-xs font-medium text-green-600 dark:text-green-400">
                      <CheckIcon />
                      {TRIAL_DAYS}-day free trial (first-time only)
                    </li>
                  ) : null}
                </ul>

                {isCurrent ? (
                  <div className="rounded-md bg-primary/10 px-4 py-2 text-center text-sm font-medium text-primary">
                    Current Plan
                  </div>
                ) : (
                  <Button
                    className={`w-full gap-1.5 ${
                      isUpgrade ? "" : isDowngrade ? "variant-outline" : isTrialEligible ? "bg-green-600 hover:bg-green-700" : ""
                    }`}
                    variant={isDowngrade ? "outline" : "default"}
                    onClick={() => handleSelectPlan(plan.id as PricingPlanId)}
                    disabled={loadingPlan !== null}
                  >
                    {loadingPlan === plan.id ? (
                      "Loading..."
                    ) : (
                      <>
                        {isUpgrade && <ArrowUpIcon />}
                        {isDowngrade && <ArrowDownIcon />}
                        {buttonLabel}
                      </>
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-muted">
          Upgrading starts a new billing cycle immediately. Downgrading is immediate with prorated credit. No refunds for unused time.
        </p>
      </div>

      {/* Plan change confirmation dialog */}
      <ConfirmDialog
        open={pendingPlanChange !== null}
        title={confirmDialogText.title}
        description={confirmDialogText.description}
        confirmText={confirmDialogText.confirmText}
        cancelText="Keep current plan"
        variant={pendingPlanChange?.isDowngrade ? "destructive" : "default"}
        onConfirm={handleConfirmPlanChange}
        onCancel={() => setPendingPlanChange(null)}
      />
    </div>
  );
}
